/**
 * Unsharp Mask (USM) — classical sharpening via blur subtraction.
 *
 *     out = orig + amount · (orig - blur(orig, σ))
 *
 * The "mask" is the high-pass residual `orig − blur`. Adding `amount ×`
 * of the mask back to the original boosts high-frequency content while
 * preserving the low-frequency structure (since `blur` carries it). A
 * `threshold` parameter clamps the mask near zero on low-contrast pixels,
 * so flat sky / skin doesn't get noise amplified.
 *
 * Distinct from `capture-sharpen.js`:
 *
 *   • capture-sharpen is iterative deconvolution (Richardson-Lucy) with
 *     a fixed σ=0.7 PSF — corrects the camera AA filter on RAW input.
 *     One-shot ingest pass, default ON for RAW.
 *
 *   • unsharp-mask is single-pass spatial sharpening with user-tunable
 *     σ, amount, and threshold — a creative-edit step the user reaches
 *     for to add bite. Default OFF; commits destructively on `srcFloat`.
 *
 * Pipeline slot: destructive pre-kernel commit. Caller path mirrors
 * Gaussian Blur (snapshot → applyUnsharpMask → buildPreviewSource).
 *
 * Identity short-circuit: amount = 0 OR sigma ≤ 0 → no-op.
 *
 * References:
 *   • Gonzalez & Woods, "Digital Image Processing" 4th ed., §3.6.3 —
 *     standard USM formulation.
 *
 * License: BLRRD is MIT. NO GPLv3 source consulted.
 *
 * @module src/pipeline/filters/spatial/unsharp-mask
 */

import { gaussianKernel1D, applyGaussian1D } from '../../capture-sharpen.js';
import { validateRGBA } from './_util.js';

/** Slider ranges. */
export const USM_SIGMA_MIN     = 0.1;
export const USM_SIGMA_MAX     = 50;
export const USM_AMOUNT_MIN    = 0;
export const USM_AMOUNT_MAX    = 4;
export const USM_THRESHOLD_MIN = 0;
export const USM_THRESHOLD_MAX = 1;

const IDENTITY_EPS = 1e-6;

/**
 * Default USM parameters. Used by the dispatcher to seed the UI.
 *
 * Defaults are identity (amount = 0). Once the user commits a non-zero
 * value, the modal stores the last-used preset on E.spatialFilters.usm
 * so re-opening the dialog restores their previous picks.
 *
 * @returns {{ sigma: number, amount: number, threshold: number }}
 */
export function defaultParams() {
    return { sigma: 2.0, amount: 0, threshold: 0.02 };
}

/**
 * Identity test for the default-params short-circuit.
 *
 * @param {{ sigma?: number, amount?: number }} params
 * @returns {boolean}
 */
export function isIdentityUSM(params) {
    if (!params) return true;
    const s = +params.sigma;
    const a = +params.amount;
    if (!Number.isFinite(s) || !Number.isFinite(a)) return true;
    if (a <= IDENTITY_EPS) return true;
    if (s <= IDENTITY_EPS) return true;
    return false;
}

/**
 * Apply Unsharp Mask in-place on a packed RGBA Float32 buffer.
 *
 * Algorithm (per-channel, RGB only; alpha preserved):
 *   1. blur := gaussian(orig, σ)         — separable pre-allocated
 *   2. for each pixel:
 *        mask := orig − blur
 *        if |mask| ≥ threshold: out := orig + amount · mask
 *        else:                  out := orig            ← below-noise
 *
 * Memory: a single Float32(w·h·4) scratch buffer is allocated to hold
 * the blurred copy. For 4K (~33 MB) this is below the ~480 MB transient
 * the capture-sharpen pipeline tolerates.
 *
 * @param {Float32Array} rgba    scene-linear ACEScg RGBA, length 4·w·h
 * @param {number} w
 * @param {number} h
 * @param {object} params
 * @param {number} params.sigma     Gaussian σ in pixels
 * @param {number} params.amount    mask scale, typical 0.5..1.5
 * @param {number} [params.threshold]  contrast floor in linear units
 * @returns {Float32Array}       same `rgba` reference (in-place)
 */
export function applyUnsharpMask(rgba, w, h, params) {
    validateRGBA('applyUnsharpMask', rgba, w, h);
    const p = params || {};
    if (isIdentityUSM(p)) return rgba;

    const sigma     = Math.min(USM_SIGMA_MAX,     Math.max(USM_SIGMA_MIN,     +p.sigma));
    const amount    = Math.min(USM_AMOUNT_MAX,    Math.max(USM_AMOUNT_MIN,    +p.amount));
    const threshold = Math.min(USM_THRESHOLD_MAX, Math.max(USM_THRESHOLD_MIN, +p.threshold || 0));

    /* Build the blurred copy. We allocate a scratch full RGBA so we
     * can run the existing capture-sharpen separable 1D pass without
     * planar conversion. The scratch is GC-eligible after this fn. */
    const blur = new Float32Array(rgba.length);
    blur.set(rgba);
    const kernel = gaussianKernel1D(sigma);
    applyGaussian1D(blur, w, h, kernel, 0);
    applyGaussian1D(blur, w, h, kernel, 1);

    /* Per-pixel: residual = orig − blur, gated by threshold. */
    const N = w * h;
    for (let i = 0, j = 0; i < N; i++, j += 4) {
        for (let c = 0; c < 3; c++) {
            const o = rgba[j + c];
            const m = o - blur[j + c];
            if (m >= threshold || m <= -threshold) {
                rgba[j + c] = o + amount * m;
            }
            /* else: leave rgba[j + c] alone — below-threshold mask is
             * treated as noise floor and left untouched. */
        }
        /* alpha (j + 3) intentionally untouched. */
    }
    return rgba;
}
